import React, { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, Tag, Tooltip } from 'antd';
import type { TableColumnsType, TableColumnType } from 'antd';
import { useSelector } from 'react-redux';

import { BUY, selectors, TRANSACTION_TYPES } from 'state';
import type { ExchangeHistory } from 'state';
import { formatters } from 'services';
import { useTableScrollY } from './useTableScrollY';

type V5ExchangeRow = Omit<
  ExchangeHistory,
  'base_currency' | 'variable_currency' | 'transaction'
> & {
  key: string;
  transaction: number;
  transaction_label: string;
  base_currency: string;
  variable_currency: string;
};

const assetSearchProps: Pick<
  TableColumnType<V5ExchangeRow>,
  'filterDropdown' | 'filterIcon' | 'onFilter'
> = {
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <div style={{ padding: 8 }} onKeyDown={event => event.stopPropagation()}>
      <Input
        autoFocus
        allowClear
        placeholder="Search asset"
        value={selectedKeys[0] as string}
        onChange={event =>
          setSelectedKeys(event.target.value ? [event.target.value] : [])
        }
        onPressEnter={() => confirm()}
        style={{ width: 180, marginBottom: 8 }}
      />
      <Space>
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => confirm()}
          style={{ width: 84 }}
        >
          Search
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearFilters?.();
            confirm();
          }}
          style={{ width: 84 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
  ),
  onFilter: (value, record) =>
    String(record.underlying_asset).includes(String(value)),
};

const CashExchange: React.FC = () => {
  const exchangeHistory = useSelector(selectors.exchangeHistory);
  const currencies = useSelector(selectors.currencies);
  const { fetching_blotter: fetchingBlotter } = useSelector(selectors.player);
  const tableScroll = useTableScrollY();
  const [assetFilter, setAssetFilter] = useState<React.Key[]>([]);

  const data = useMemo<V5ExchangeRow[]>(
    () =>
      exchangeHistory
        .map((item, index) => ({
          ...item,
          key: `${item.timestamp}-${index}`,
          transaction: item.transaction,
          transaction_label: TRANSACTION_TYPES[item.transaction],
          base_currency: currencies[item.base_currency]?.symbol ?? '',
          variable_currency: currencies[item.variable_currency]?.symbol ?? '',
        }))
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    [exchangeHistory, currencies],
  );

  const columns: TableColumnsType<V5ExchangeRow> = [
    {
      title: 'Asset',
      dataIndex: 'underlying_asset',
      key: 'underlying_asset',
      fixed: 'left',
      width: 90,
      sorter: (a, b) => a.underlying_asset - b.underlying_asset,
      ...assetSearchProps,
      filteredValue: assetFilter.length ? assetFilter : null,
      onCell: record => ({
        onClick: () =>
          setAssetFilter(current =>
            current.length === 1 && current[0] === record.underlying_asset
              ? []
              : [record.underlying_asset],
          ),
        style: { cursor: 'pointer' },
      }),
      render: (assetId: number) => {
        const active = assetFilter.length === 1 && assetFilter[0] === assetId;
        return (
          <Tooltip
            placement="left"
            title={
              active
                ? 'Filtering by this asset — click to clear'
                : 'Click to filter the blotter by this asset'
            }
          >
            <span className="blotter-v5__asset-link">{assetId}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Transaction',
      dataIndex: 'transaction_label',
      key: 'transaction_label',
      width: 104,
      render: (label, record) => (
        <Tag
          color={record.transaction === BUY ? 'green' : 'red'}
          style={{ marginInlineEnd: 0 }}
        >
          {label}
        </Tag>
      ),
    },
    {
      title: 'Base Currency',
      dataIndex: 'base_currency',
      key: 'base_currency',
      width: 112,
      sorter: (a, b) => a.base_currency.localeCompare(b.base_currency),
    },
    {
      title: 'Variable Currency',
      dataIndex: 'variable_currency',
      key: 'variable_currency',
      width: 126,
      sorter: (a, b) => a.variable_currency.localeCompare(b.variable_currency),
    },
    {
      title: 'Exchange Rate',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 116,
      align: 'right',
      render: (value, record) =>
        record.base_currency === 'JPY' || record.variable_currency === 'JPY'
          ? formatters.commasExact2Digits(value)
          : formatters.commasExact4Digits(value),
    },
    {
      title: 'Quantity',
      dataIndex: 'amount',
      key: 'amount',
      width: 104,
      align: 'right',
      render: formatters.commasNoDigits,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 104,
      align: 'right',
      render: formatters.commasNoDigits,
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 154,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.timestamp.localeCompare(b.timestamp),
      render: value =>
        value ? (
          formatters.dateWithTimeSeconds(value)
        ) : (
          <span className="blotter-v5__muted">—</span>
        ),
    },
  ];

  return (
    <section className="blotter-v5__card" aria-label="Cash exchange blotter">
      <header className="blotter-v5__card-head">
        <span className="blotter-v5__card-title">
          Cash Exchange
          {data.length > 0 && (
            <span className="blotter-v5__card-hint">
              {' — '}click an asset to filter
            </span>
          )}
        </span>
        <span className="blotter-v5__count">
          {data.length} {data.length === 1 ? 'exchange' : 'exchanges'}
        </span>
      </header>
      <div ref={tableScroll.ref} className="blotter-v5__table-wrap">
        <Table<V5ExchangeRow>
          size="small"
          rowKey="key"
          loading={fetchingBlotter}
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ x: 'max-content', y: tableScroll.y }}
          onChange={(_pagination, filters) =>
            setAssetFilter((filters.underlying_asset as React.Key[]) ?? [])
          }
        />
      </div>
    </section>
  );
};

export default CashExchange;
